import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tip } from './ui/tip';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { confirmDialog } from './ConfirmDialog';

/**
 * Condiciones de pago escalonadas de cta cte (estilo Vetaro) — administración.
 * FEATURE DORMIDA: el interruptor maestro decide si las emisiones nuevas de
 * cta cte congelan la escalera en el documento. Apagado = cero efecto.
 */

interface Tramo { hastaDias: number; pct: number }
interface Condicion {
  id: string;
  name: string;
  isDefault: number;
  tramos: Tramo[];
  moraMensualPct: number | null;
}
interface ClienteCC {
  id: string;
  name: string;
  balance: number;
  paymentConditionId: string | null;
  paymentConditionCustom: Tramo[] | null;
}

function TramosEditor({ tramos, onChange }: { tramos: Tramo[]; onChange: (t: Tramo[]) => void }) {
  const set = (i: number, campo: keyof Tramo, valor: string) => {
    const next = tramos.map((t, j) => (j === i ? { ...t, [campo]: valor === '' ? ('' as any) : Number(valor) } : t));
    onChange(next);
  };
  return (
    <div className="space-y-1.5">
      {tramos.map((t, i) => (
        <div key={i} className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground w-24">{i === 0 ? 'Pago 0 a' : `${(Number(tramos[i - 1]?.hastaDias) || 0) + 1} a`}</span>
          <Input type="number" min={0} value={t.hastaDias as any} onChange={(e) => set(i, 'hastaDias', e.target.value)} className="h-8 w-20" />
          <span className="text-muted-foreground">días:</span>
          <Input type="number" step="0.5" value={t.pct as any} onChange={(e) => set(i, 'pct', e.target.value)} className="h-8 w-20" />
          <span className="text-muted-foreground">% (− desc / + recargo)</span>
          <Tip label="Quitar tramo">
            <button type="button" onClick={() => onChange(tramos.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-red-600">
              <TrashIcon className="h-4 w-4" />
            </button>
          </Tip>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="h-7 gap-1"
        onClick={() => onChange([...tramos, { hastaDias: (Number(tramos[tramos.length - 1]?.hastaDias) || 0) + 30, pct: 0 }])}>
        <PlusIcon className="h-3.5 w-3.5" /> Agregar tramo
      </Button>
    </div>
  );
}

const resumenTramos = (tramos: Tramo[]) => {
  let desde = 0;
  return tramos.map((t) => {
    const s = `${desde}-${t.hastaDias}d: ${t.pct > 0 ? '+' : ''}${t.pct}%`;
    desde = t.hastaDias + 1;
    return s;
  }).join(' · ');
};

export default function PaymentConditionsSettings() {
  const [enabled, setEnabled] = useState(false);
  const [conditions, setConditions] = useState<Condicion[]>([]);
  const [clients, setClients] = useState<ClienteCC[]>([]);
  const [loading, setLoading] = useState(true);
  // editor de condicion (crear/editar)
  const [editando, setEditando] = useState<{ id: string | null; name: string; tramos: Tramo[]; mora: string } | null>(null);
  // editor de escalera personalizada por cliente
  const [customEdit, setCustomEdit] = useState<{ clientId: string; tramos: Tramo[] } | null>(null);

  const cargar = useCallback(async () => {
    try {
      const [condRes, cliRes] = await Promise.all([
        axios.get('/api/payment-conditions'),
        axios.get('/api/payment-conditions/clients')
      ]);
      setEnabled(!!condRes.data.enabled);
      setConditions(condRes.data.conditions || []);
      setClients(cliRes.data.clients || []);
    } catch {
      toast.error('No se pudieron cargar las condiciones de pago');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const toggleMaster = async (v: boolean) => {
    try {
      await axios.put('/api/payment-conditions/enabled', { enabled: v });
      setEnabled(v);
      toast.success(v ? 'Condiciones de pago ACTIVADAS: las emisiones nuevas de cta cte llevan escalera' : 'Condiciones de pago desactivadas');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'No se pudo cambiar el interruptor');
    }
  };

  const guardarCondicion = async () => {
    if (!editando) return;
    const payload = {
      name: editando.name,
      tramos: editando.tramos,
      moraMensualPct: editando.mora === '' ? null : Number(editando.mora)
    };
    try {
      if (editando.id) await axios.put(`/api/payment-conditions/${editando.id}`, payload);
      else await axios.post('/api/payment-conditions', payload);
      toast.success('Condición guardada');
      setEditando(null);
      await cargar();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'No se pudo guardar');
    }
  };

  const borrarCondicion = async (c: Condicion) => {
    if (!await confirmDialog(`¿Borrar la condición "${c.name}"? Los clientes asignados pasan a heredar la default.`)) return;
    try {
      await axios.delete(`/api/payment-conditions/${c.id}`);
      await cargar();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'No se pudo borrar');
    }
  };

  const asignar = async (clientId: string, conditionId: string | null, custom: Tramo[] | null) => {
    try {
      await axios.put(`/api/payment-conditions/assign/${clientId}`, { conditionId, custom });
      await cargar();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'No se pudo asignar');
    }
  };

  if (loading) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle>Condiciones de pago (cta cte)</CardTitle>
            <CardDescription>
              Escaleras estilo "pronto pago": se factura al máximo y el que paga antes paga menos.
              La escalera queda impresa y congelada en cada remito de cuenta corriente.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm text-muted-foreground">{enabled ? 'Activo' : 'Dormido'}</span>
            <Switch checked={enabled} onCheckedChange={toggleMaster} />
          </div>
        </div>
        {!enabled && (
          <p className="text-xs text-amber-600 dark:text-amber-500">
            Dormido: podés armar las escaleras y asignarlas tranquilo — no afecta ninguna emisión hasta activar.
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Condiciones (grupos) */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Escaleras</h4>
            <Button type="button" variant="outline" size="sm" className="h-7 gap-1"
              onClick={() => setEditando({ id: null, name: '', tramos: [{ hastaDias: 7, pct: -15 }, { hastaDias: 30, pct: -10 }, { hastaDias: 60, pct: 0 }], mora: '' })}>
              <PlusIcon className="h-3.5 w-3.5" /> Nueva
            </Button>
          </div>
          {conditions.length === 0 && <p className="text-sm text-muted-foreground">Sin escaleras todavía. Creá la primera (queda como default).</p>}
          {conditions.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-2 rounded-lg border p-2.5 text-sm">
              <span className="font-medium">{c.name}</span>
              {Number(c.isDefault) === 1 && <Badge variant="secondary">Default</Badge>}
              <span className="text-xs text-muted-foreground">{resumenTramos(c.tramos)}</span>
              {c.moraMensualPct != null && <span className="text-xs text-muted-foreground">· mora {c.moraMensualPct}%/mes</span>}
              <span className="ml-auto flex items-center gap-1">
                {Number(c.isDefault) !== 1 && (
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-xs"
                    onClick={async () => { await axios.post(`/api/payment-conditions/${c.id}/default`); await cargar(); }}>
                    Hacer default
                  </Button>
                )}
                <Tip label="Editar">
                  <Button type="button" variant="outline" size="icon" className="size-7"
                    onClick={() => setEditando({ id: c.id, name: c.name, tramos: c.tramos, mora: c.moraMensualPct != null ? String(c.moraMensualPct) : '' })}>
                    <PencilIcon className="h-3.5 w-3.5" />
                  </Button>
                </Tip>
                <Tip label="Borrar">
                  <Button type="button" variant="outline" size="icon" className="size-7 text-red-600" onClick={() => borrarCondicion(c)}>
                    <TrashIcon className="h-3.5 w-3.5" />
                  </Button>
                </Tip>
              </span>
            </div>
          ))}

          {editando && (
            <div className="space-y-3 rounded-lg border border-primary/40 bg-primary/5 p-3">
              <div className="flex items-center gap-2">
                <Input placeholder="Nombre (ej: Empresa cta cte)" value={editando.name}
                  onChange={(e) => setEditando({ ...editando, name: e.target.value })} className="h-8 max-w-xs" />
                <span className="text-sm text-muted-foreground">Mora</span>
                <Input type="number" step="0.5" min={0} placeholder="—" value={editando.mora}
                  onChange={(e) => setEditando({ ...editando, mora: e.target.value })} className="h-8 w-20" />
                <span className="text-sm text-muted-foreground">%/mes (vacío = sin definir)</span>
              </div>
              <TramosEditor tramos={editando.tramos} onChange={(t) => setEditando({ ...editando, tramos: t })} />
              <div className="flex gap-2">
                <Button type="button" size="sm" onClick={guardarCondicion}>Guardar</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setEditando(null)}>Cancelar</Button>
              </div>
            </div>
          )}
        </div>

        {/* Asignación por cliente */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Clientes con cuenta corriente</h4>
          <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
            {clients.map((cl) => (
              <div key={cl.id} className="flex flex-wrap items-center gap-2 rounded-lg border p-2 text-sm">
                <span className="min-w-0 flex-1 truncate font-medium">{cl.name || 'Sin nombre'}</span>
                {cl.paymentConditionCustom ? (
                  <>
                    <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Personalizada</Badge>
                    <span className="text-xs text-muted-foreground">{resumenTramos(cl.paymentConditionCustom)}</span>
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs"
                      onClick={() => asignar(cl.id, cl.paymentConditionId, null)}>
                      Volver a heredar
                    </Button>
                  </>
                ) : (
                  <>
                    <Select
                      value={cl.paymentConditionId || 'default'}
                      onValueChange={(v) => asignar(cl.id, v === 'default' ? null : v, null)}
                    >
                      <SelectTrigger className="h-8 w-[200px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">Default del sistema</SelectItem>
                        {conditions.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs"
                      onClick={() => {
                        const base = conditions.find((c) => c.id === cl.paymentConditionId)
                          || conditions.find((c) => Number(c.isDefault) === 1);
                        setCustomEdit({ clientId: cl.id, tramos: base?.tramos?.length ? [...base.tramos] : [{ hastaDias: 30, pct: 0 }] });
                      }}>
                      Personalizar
                    </Button>
                  </>
                )}
                {customEdit?.clientId === cl.id && (
                  <div className="w-full space-y-2 rounded-md border border-primary/40 bg-primary/5 p-2">
                    <TramosEditor tramos={customEdit.tramos} onChange={(t) => setCustomEdit({ clientId: cl.id, tramos: t })} />
                    <div className="flex gap-2">
                      <Button type="button" size="sm" onClick={async () => { await asignar(cl.id, cl.paymentConditionId, customEdit.tramos); setCustomEdit(null); }}>
                        Guardar personalizada
                      </Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => setCustomEdit(null)}>Cancelar</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {clients.length === 0 && <p className="text-sm text-muted-foreground">No hay clientes con cuenta corriente.</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
